import { useState } from "react"
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Stack, Tooltip } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { AppError, ErrorDisplay, ErrorMatcher } from "../../../../../core/api/errorTypes"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import {
    CompanyAdminWorkgroupsQuery,
    fetchWorkgroupForCompany,
    WorkgroupForCompanyResponse,
} from "../../../../../modules/company/admin/organization/workgroups/workgroupsApi"
import { matches } from "../../../../../modules/error/errorMatchers"
import { MeetingServiceOrderWorkgroup } from "../../../../../modules/merchant/accounting/accounting/accountingTypes"

import { SectionItemData } from "./common/ReceiptPrimitives"
import { WorkgroupMembersTooltip } from "./WorkgroupMembersTooltip"

const useStyles = makeStyles()((theme) => ({
    icon: {
        display: "inline-flex",
        color: theme.palette.tailwind.slate[400],
        cursor: "pointer",
        "@media print": {
            display: "none",
        },
    },
    name: {
        textAlign: "right",
    },
}))

const selectMembers = (data: WorkgroupForCompanyResponse) => data?.workgroup?.members ?? []

const ACCESS_DENIED_MATCHERS: ReadonlyArray<ErrorMatcher> = [
    { type: "response-status", match: 401 },
    { type: "response-status", match: 403 },
]

function isAccessDeniedError(error: unknown) {
    const appError = AppError.wrap(error)
    return ACCESS_DENIED_MATCHERS.some((matcher) => matches(matcher, appError))
}

type WorkgroupInfoProps = Readonly<{
    workgroup: MeetingServiceOrderWorkgroup
}>

export function WorkgroupInfo({ workgroup }: WorkgroupInfoProps) {
    const { classes } = useStyles()
    const [isTooltipOpen, setIsTooltipOpen] = useState(false)

    const membersResult = useApiQuery(fetchWorkgroupForCompany, {
        queryName: CompanyAdminWorkgroupsQuery.DETAILS,
        dependencies: { workgroupId: String(workgroup.id) },
        enabled: isTooltipOpen,
        errorDisplay: ErrorDisplay.NONE,
        select: selectMembers,
    })

    return (
        <Stack direction="row" alignItems="center" gap=".375rem">
            <SectionItemData classNames={classes.name}>{workgroup.name}</SectionItemData>
            <Tooltip
                title={
                    <WorkgroupMembersTooltip
                        isLoading={membersResult.isInitialLoading}
                        isError={membersResult.isError}
                        isAccessDenied={isAccessDeniedError(membersResult.error)}
                        members={membersResult.data ?? []}
                    />
                }
                arrow
                open={isTooltipOpen}
                onOpen={() => setIsTooltipOpen(true)}
                onClose={() => setIsTooltipOpen(false)}
            >
                <Box component="span" className={classes.icon}>
                    <FontAwesomeIcon icon={faCircleInfo} />
                </Box>
            </Tooltip>
        </Stack>
    )
}
